/*
 * Cookie-consent gate
 * --------------------
 * Turns Shopify's native privacy banner (#shopify-pc__banner / #shopify-pc__prefs)
 * into a hard blocking modal: while it is on screen we blur + dim the storefront
 * and block every click / scroll / tab behind it, then release the instant the
 * banner is gone (decision made) — or if it never really appears.
 *
 * DESIGN: the gate strictly MIRRORS the banner. It engages ONLY when a real,
 * visible banner element occupies the screen, and disengages the moment that
 * stops being true. This makes a "stuck blur" impossible: no banner => no gate.
 * (An earlier version engaged off Customer-Privacy `shouldShowBanner()`, which
 * on production can read true while Shopify renders no visible banner — that
 * left the whole site blurred with nothing to dismiss. Fixed 2026-07-16.)
 *
 * No theme/Alpine dependencies. Styling lives in cookie-banner.css.
 */
(function () {
  'use strict';

  // Never trap the merchant inside the Shopify theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  var GATE_ID = 'ms-consent-gate';
  var LOCK_CLASS = 'ms-consent-locked';
  var NUDGE_CLASS = 'ms-consent-nudge';
  var BANNER_SEL = '#shopify-pc__banner';
  // Shopify's wrapper has no rendered dimensions. The nested dialog is the
  // element that actually occupies the screen and must drive the gate.
  var PREFS_SEL = '#shopify-pc__prefs__dialog';
  // Buttons that represent an actual decision (NOT "manage preferences").
  var DECISION_BTN_SEL =
    '.shopify-pc__banner__btn-accept,.shopify-pc__banner__btn-decline,#shopify-pc__prefs__header-save,#shopify-pc__prefs__header-accept,#shopify-pc__prefs__header-decline,.shopify-pc__prefs__save';

  var engaged = false;
  var offTimer = null;

  function el(sel) {
    return document.querySelector(sel);
  }

  // A banner counts as "shown" only if it is connected, not hidden, AND
  // actually occupies space on screen. The size check is what prevents an
  // in-DOM-but-invisible banner from blurring the whole site.
  function isShown(node) {
    if (!node || !node.isConnected) return false;
    var s = window.getComputedStyle(node);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') {
      return false;
    }
    var r = node.getBoundingClientRect();
    return r.width > 1 && r.height > 1;
  }

  function bannerVisible() {
    return isShown(el(BANNER_SEL)) || isShown(el(PREFS_SEL));
  }

  /* ── Scroll lock (with scrollbar-width compensation to avoid a jump) ────── */
  function lockScroll() {
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
  }
  function unlockScroll() {
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    document.body.style.paddingRight = '';
  }

  /* ── The blocking / blur overlay ───────────────────────────────────────── */
  function ensureGate() {
    var gate = document.getElementById(GATE_ID);
    if (gate) return gate;
    gate = document.createElement('div');
    gate.id = GATE_ID;
    gate.setAttribute('aria-hidden', 'true');
    gate.addEventListener('click', onGateClick, true);
    gate.addEventListener('wheel', block, { passive: false });
    gate.addEventListener('touchmove', block, { passive: false });
    document.body.appendChild(gate);
    return gate;
  }
  function removeGate() {
    var gate = document.getElementById(GATE_ID);
    if (gate && gate.parentNode) gate.parentNode.removeChild(gate);
  }

  function block(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  // Clicking the blocked area draws attention to the banner instead of doing nothing.
  function onGateClick(e) {
    block(e);
    var banner = el(BANNER_SEL);
    if (!isShown(banner)) return; // prefs modal has its own transform — don't nudge it
    banner.classList.remove(NUDGE_CLASS);
    void banner.offsetWidth; // reflow so the animation can retrigger
    banner.classList.add(NUDGE_CLASS);
  }

  /* ── Keep keyboard focus inside the banner while gated ─────────────────── */
  function trapKeys(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key !== 'Tab') return;
    var modal = isShown(el(PREFS_SEL)) ? el(PREFS_SEL) : el(BANNER_SEL);
    if (!modal) return;
    var focusables = modal.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (!modal.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── Engage / disengage ────────────────────────────────────────────────── */
  function engage() {
    if (engaged) return;
    engaged = true;
    ensureGate();
    lockScroll();
    document.addEventListener('keydown', trapKeys, true);
  }
  function disengage() {
    if (!engaged) return;
    engaged = false;
    document.removeEventListener('keydown', trapKeys, true);
    unlockScroll();
    removeGate();
  }

  // The gate mirrors the banner. Visible banner -> gate on. No banner -> gate
  // off (debounced slightly so the banner→prefs swap, which can briefly show
  // neither element, doesn't flicker the overlay).
  function sync() {
    if (bannerVisible()) {
      if (offTimer) {
        clearTimeout(offTimer);
        offTimer = null;
      }
      engage();
    } else if (engaged && !offTimer) {
      offTimer = window.setTimeout(function () {
        offTimer = null;
        if (!bannerVisible()) disengage();
      }, 200);
    }
  }

  /* ── Signals ───────────────────────────────────────────────────────────── */

  // Decision events / clicks just trigger a re-sync; the banner disappearing
  // is what actually releases the gate.
  document.addEventListener('visitorConsentCollected', function () {
    window.setTimeout(sync, 0);
  });
  document.addEventListener(
    'click',
    function (e) {
      if (e.target && e.target.closest && e.target.closest(DECISION_BTN_SEL)) {
        window.setTimeout(sync, 60);
      }
    },
    true
  );

  function start() {
    sync();

    // React quickly to Shopify injecting / removing the banner.
    var observer = new MutationObserver(function () {
      if (observer._raf) return;
      observer._raf = window.requestAnimationFrame(function () {
        observer._raf = null;
        sync();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Safety net: a low-frequency poll guarantees the gate can never outlive
    // the banner regardless of how Shopify shows/hides it.
    window.setInterval(sync, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
